<template>
    <!-- Page Header -->
    <div class="mb-4">
        <div class="flex items-center justify-between">
            <div>
                <h1
                    class="text-2xl font-semibold text-gray-900 dark:text-white"
                >
                    {{ t("dashboard.title") }}
                </h1>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {{ t("dashboard.welcome", { name: user?.name || "User" }) }}
                </p>
            </div>
        </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex justify-center items-center h-64">
        <div
            class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"
        ></div>
    </div>

    <!-- Error State -->
    <div
        v-else-if="error"
        class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4"
    >
        <div class="flex">
            <TriangleAlert class="h-5 w-5 text-red-400" />
            <div class="ml-3">
                <h3 class="text-sm font-medium text-red-800 dark:text-red-200">
                    {{ t("messages.loadError") }}
                </h3>
                <div class="mt-2 text-sm text-red-700 dark:text-red-300">
                    {{ error }}
                </div>
                <button
                    @click="fetchDashboardData"
                    class="mt-3 text-sm font-medium text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300"
                >
                    {{ t("common.retry") }}
                </button>
            </div>
        </div>
    </div>

    <!-- Role-based Dashboard Content -->
    <div v-else-if="!error">
        <!-- Super Admin Dashboard -->
        <SuperAdminDashboard
            v-if="userRole === 'super-administrator' && !error && dashboardData"
            :data="dashboardData"
        />

        <!-- Administrator Dashboard -->
        <AdministratorDashboard
            v-else-if="userRole === 'administrator' && !error && dashboardData"
            :data="dashboardData"
        />

        <!-- Teacher Dashboard -->
        <TeacherDashboard
            v-else-if="userRole === 'teacher' && !error && dashboardData"
            :data="dashboardData"
        />

        <!-- Student Dashboard -->
        <StudentDashboard
            v-else-if="userRole === 'student' && !error && dashboardData"
            :data="dashboardData"
        />

        <!-- Fallback for unknown roles -->
        <div v-else class="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <div class="text-center">
                <TriangleAlert class="mx-auto h-12 w-12 text-gray-400" />
                <h3
                    class="mt-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                    {{ t("dashboard.notAvailable") }}
                </h3>
                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {{ t("dashboard.roleNotConfigured", { role: userRole }) }}
                </p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useAuthStore } from "@/stores/authStore";
import { useI18n } from "vue-i18n";
import DashboardService from "@/api/dashboard";
import type {
    SuperAdminDashboardData,
    AdministratorDashboardData,
    TeacherDashboardData,
    StudentDashboardData,
} from "@/types/dashboard";
import SuperAdminDashboard from "@/components/dashboard/SuperAdminDashboard.vue";
import AdministratorDashboard from "@/components/dashboard/AdministratorDashboard.vue";
import TeacherDashboard from "@/components/dashboard/TeacherDashboard.vue";
import StudentDashboard from "@/components/dashboard/StudentDashboard.vue";
import { TriangleAlert } from "@lucide/vue";

const authStore = useAuthStore();
const { t } = useI18n();

// State
const isLoading = ref(false);
const error = ref<string | null>(null);
const dashboardData = ref<any>(null);

// Computed
const user = computed(() => authStore.user);
const userRole = computed(() => {
    const roles = authStore.userRoles;
    if (roles.length === 0) return null;

    // Return the highest priority role
    const roleHierarchy = [
        "super-administrator",
        "administrator",
        "teacher",
        "student",
    ];

    for (const role of roleHierarchy) {
        if (authStore.hasRole(role)) {
            return role;
        }
    }

    return roles[0].name;
});

// Methods
const fetchDashboardData = async () => {
    if (!userRole.value) return;

    try {
        isLoading.value = true;
        error.value = null;

        dashboardData.value = await DashboardService.getDashboardData(
            userRole.value,
        );
    } catch (err: any) {
        console.error("Error fetching dashboard data:", err);
        error.value =
            err.response?.data?.message ||
            err.message ||
            "Failed to load dashboard data";

        // Don't provide mock data - let the error state show
        dashboardData.value = null;
    } finally {
        isLoading.value = false;
    }
};

watch(
    () => authStore.isInitialized,
    (initialized) => {
        if (initialized && authStore.isAuthenticated) {
            fetchDashboardData();
        } else if (initialized && !authStore.isAuthenticated) {
            error.value = "Please login to continue";
        }
    },
    { immediate: true },
);
</script>
